import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiUsers } from 'react-icons/fi';
import { BACKEND_MAIN_URL } from '../config';

/**
 * Landing page for an emailed project invitation: /join/<token> (#72).
 *
 *  - signed in as the invited address → joins and opens the project;
 *  - signed in as someone else        → says which address to use;
 *  - not signed in, account exists     → log in, then come back here;
 *  - not signed in, no account         → sign up with the address prefilled.
 *
 * Joining always happens server-side against the signed-in account, so a
 * forwarded link cannot hand the project to a different person.
 */
const JoinProject = ({ isAuthenticated }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | joining | invalid | wrong_account | error
  const [invitedEmail, setInvitedEmail] = useState('');

  const here = `/join/${encodeURIComponent(token)}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_MAIN_URL}/api/invites/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setInvite(data);
        setInvitedEmail(data.email || '');
        if (!isAuthenticated) { setState('ready'); return; }
        setState('joining');
        const authToken = localStorage.getItem('token');
        const resp = await axios.post(
          `${BACKEND_MAIN_URL}/api/invites/${encodeURIComponent(token)}/accept`, {},
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (!cancelled) navigate(`/projects/${resp.data.project_id}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const code = err.response?.data?.error;
        if (status === 404) setState('invalid');
        else if (code === 'wrong_account') { setInvitedEmail(err.response.data.invited_email || ''); setState('wrong_account'); }
        else setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, isAuthenticated, navigate]);

  const authUrl = (mode) => {
    const params = new URLSearchParams({ redirect: here });
    if (mode === 'signup') params.set('mode', 'signup');
    if (invitedEmail) params.set('email', invitedEmail);
    return `/login?${params.toString()}`;
  };

  const switchAccount = () => {
    // Full reload so the whole app forgets the other session.
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    window.location.assign(authUrl(invite?.account_exists ? 'login' : 'signup'));
  };

  const role = invite?.role === 'editor' ? t('share.role_editor', 'Editor') : t('share.role_viewer', 'Viewer');

  return (
    <main className="join-page">
      <section className="join-card" aria-live="polite">
        <FiUsers className="join-icon" aria-hidden="true" />
        {state === 'loading' && <p>{t('join.loading', 'Opening your invitation…')}</p>}
        {state === 'joining' && <p>{t('join.joining', 'Joining the project…')}</p>}
        {state === 'invalid' && <>
          <h1>{t('join.invalid_title', 'This invitation is no longer valid')}</h1>
          <p>{t('join.invalid_body', 'The link may have been revoked. Ask the project owner to invite you again.')}</p>
        </>}
        {state === 'error' && <>
          <h1>{t('join.error_title', 'Something went wrong')}</h1>
          <p>{t('join.error_body', 'We could not open this invitation. Please try again in a moment.')}</p>
        </>}
        {(state === 'ready' || state === 'wrong_account') && invite && <>
          <h1>{t('join.title', 'Join “{{project}}”', { project: invite.project_name || t('join.a_project', 'a project') })}</h1>
          <p>{invite.inviter
            ? t('join.invited_by', '{{who}} invited you as {{role}}.', { who: invite.inviter, role })
            : t('join.invited', 'You were invited as {{role}}.', { role })}</p>
          {state === 'wrong_account' ? <>
            <p className="join-warning">{t('join.wrong_account', 'This invitation was sent to {{email}}. You are signed in with a different account.', { email: invitedEmail })}</p>
            <button type="button" className="join-primary" onClick={switchAccount}>{t('join.switch', 'Sign in as {{email}}', { email: invitedEmail })}</button>
          </> : invite.account_exists ? <>
            <p>{t('join.have_account', 'Sign in with {{email}} to join.', { email: invitedEmail })}</p>
            <button type="button" className="join-primary" onClick={() => navigate(authUrl('login'))}>{t('join.login', 'Sign in and join')}</button>
          </> : <>
            <p>{t('join.no_account', 'Create your free UpyaChat account with {{email}} to join.', { email: invitedEmail })}</p>
            <button type="button" className="join-primary" onClick={() => navigate(authUrl('signup'))}>{t('join.signup', 'Create my account')}</button>
            <button type="button" className="join-secondary" onClick={() => navigate(authUrl('login'))}>{t('join.already', 'I already have an account')}</button>
          </>}
        </>}
      </section>
    </main>
  );
};

export default JoinProject;
